import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { operationsClient, apiGet, apiPost } from '@/lib/api';
import { schedulerEndpoints } from '@/lib/api/endpoints';

export type TechLiveState =
  | 'OFFLINE'
  | 'AVAILABLE'
  | 'EN_ROUTE'
  | 'ON_SITE'
  | 'WRAPPING_UP'
  | 'BREAK';

export interface LiveTechAppointment {
  id: string;
  customerName: string;
  /** NOT-334 — job type for the "{service} · {customer}" card line. Optional so a
   *  stale/staggered API response with the pre-NOT-334 shape can't blank the page. */
  serviceType?: { name: string } | null;
  status: string;
  scheduledStart: string | null;
  arrivalTime: string | null;
  /** NOT-334 — on-site duration is now − (startedAt ?? arrivedAt). */
  arrivedAt: string | null;
  startedAt: string | null;
  lateMinutes: number | null;
  onTime: boolean | null;
  /** §8.6 — geocoded job destination, for the arrival geofence ring on the map. */
  destination?: { lat: number; lng: number } | null;
}

/** Why the tech's dot is (or isn't) trustworthy right now — the observability
 *  signal so a frozen marker never reads as live. */
export type TechSharingReason = 'live' | 'stale' | 'paused' | 'not_sharing';

export interface TechSharing {
  reason: TechSharingReason;
  lastPingAt: string | null;
  ageSeconds: number | null;
}

export interface LiveTech {
  technicianId: string;
  name: string;
  state: TechLiveState;
  /** Location-sharing health, independent of `state`. Optional for back-compat
   *  with a pre-field API response. */
  sharing?: TechSharing;
  lastSeenAt: string | null;
  stale: boolean;
  /** NOT-334 — when an AVAILABLE tech went idle (last job end, else clock-in). */
  idleSinceAt: string | null;
  location: { lat: number; lng: number } | null;
  currentAppointment: LiveTechAppointment | null;
}

export interface DispatchLiveView {
  generatedAt: string;
  timezone: string;
  techs: LiveTech[];
  variance: {
    dayNetMin: number;
    byTech: {
      technicianId: string;
      name: string;
      netStartVarianceMin: number;
      // §15.3 — null until at least one appointment is completed (completedAt).
      netEndVarianceMin: number | null;
      netDurationVarianceMin: number | null;
      appointments: number;
      completed: number;
    }[];
  };
  /** NOT-333 — count of today's customer ETA/late auto-texts (business tz). */
  autoTextsToday: number;
}

export interface DispatchAlert {
  id: string;
  technicianId: string | null;
  appointmentId: string | null;
  /** NOT-336 — the appointment's job, so "Reschedule" can open the visit drawer. */
  jobId: string | null;
  type: string;
  message: string;
  /** NOT-336 — per-type extras (deltaMinutes / elapsedMinutes / offRouteMeters / preview). */
  detail: Record<string, unknown> | null;
  createdAt: string;
  /** NOT-336 — latest ETA/on-the-way auto-text sent for this appointment, or null. */
  customerNotifiedAt: string | null;
}

const REFRESH_MS = 30_000;

/** §15 — the live dispatch aggregate, polled at the tracking-page cadence. */
export function useDispatchLive(opts: { enabled?: boolean } = {}) {
  return useApiQuery<DispatchLiveView>({
    queryKey: ['dispatch', 'live'],
    queryFn: () => apiGet<DispatchLiveView>(operationsClient, schedulerEndpoints.dispatchLive()),
    enabled: opts.enabled ?? true,
    showErrorToast: false,
    refetchInterval: REFRESH_MS,
  });
}

/** §15.4/§9.5 — the open dispatcher alert feed. */
export function useDispatchAlerts() {
  return useApiQuery<DispatchAlert[]>({
    queryKey: ['dispatch', 'alerts'],
    queryFn: () => apiGet<DispatchAlert[]>(operationsClient, schedulerEndpoints.trackingAlerts()),
    showErrorToast: false,
    refetchInterval: REFRESH_MS,
  });
}

/** NOT-335 — a geocoded stop on a technician's day route (live map polyline). */
export interface TechRouteStop {
  id: string;
  lat: number;
  lng: number;
  label: string;
  customerName: string;
  time: string | null;
  status: string;
}

/** NOT-335 — the focused tech's day route; only fetched while a tech is focused. */
export function useTechDayRoute(technicianId: string | null | undefined) {
  return useApiQuery<TechRouteStop[]>({
    queryKey: ['dispatch', 'live', 'route', technicianId],
    queryFn: () =>
      apiGet<TechRouteStop[]>(
        operationsClient,
        schedulerEndpoints.dispatchLiveRoute(technicianId!),
      ),
    enabled: Boolean(technicianId),
    showErrorToast: false,
    refetchInterval: REFRESH_MS,
  });
}

/**
 * Day routes for MANY technicians at once, so the live map can draw every tech's
 * geocoded jobs (pins + geofence rings) without waiting for one to be focused.
 * Shares the same per-tech query key as `useTechDayRoute`, so the focused tech's
 * route is deduped, not re-fetched. Returns a memoized `techId -> stops` map
 * (via react-query `combine`, so the reference is stable across re-renders).
 */
export function useAllTechDayRoutes(technicianIds: string[]): Record<string, TechRouteStop[]> {
  return useQueries({
    queries: technicianIds.map((id) => ({
      queryKey: ['dispatch', 'live', 'route', id],
      queryFn: () =>
        apiGet<TechRouteStop[]>(operationsClient, schedulerEndpoints.dispatchLiveRoute(id)),
      enabled: Boolean(id),
      refetchInterval: REFRESH_MS,
      // These raw useQueries bypass useApiQuery, so opt them into the same
      // global-toast suppression: a transient outage must not fire one error
      // toast per visible tech (50-100 on a big board).
      meta: { suppressToast: true },
    })),
    combine: (results) => {
      const byTech: Record<string, TechRouteStop[]> = {};
      technicianIds.forEach((id, i) => {
        byTech[id] = results[i]?.data ?? [];
      });
      return byTech;
    },
  });
}

/** Dismiss a dispatcher alert. */
export function useResolveDispatchAlert() {
  const queryClient = useQueryClient();
  return useApiMutation<{ resolved: boolean }, { id: string }>({
    mutationFn: ({ id }) =>
      apiPost(operationsClient, schedulerEndpoints.resolveTrackingAlert(id), {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dispatch', 'alerts'] });
    },
  });
}

/** NOT-336 — send the customer the ETA/late tracking SMS for an alert's job.
 *  `alreadyNotified` is true when the customer had already been texted for this
 *  alert (auto pipeline or a concurrent click) and nothing new was sent. */
export function useNotifyCustomerAlert() {
  const queryClient = useQueryClient();
  return useApiMutation<{ notified: boolean; alreadyNotified?: boolean }, { id: string }>({
    mutationFn: ({ id }) =>
      apiPost(operationsClient, schedulerEndpoints.notifyCustomerAlert(id), {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dispatch', 'alerts'] });
    },
  });
}

/** NOT-336 — message an alert's technician by SMS. */
export function useMessageTechAlert() {
  const queryClient = useQueryClient();
  return useApiMutation<{ delivered: boolean }, { id: string; text: string }>({
    mutationFn: ({ id, text }) =>
      apiPost(operationsClient, schedulerEndpoints.messageTechAlert(id), { text }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dispatch', 'alerts'] });
    },
  });
}
